import {
  UserProfile,
  UserProfileRecord,
  ConsultationRecord,
  PracticeKundaliRecord,
  UserRole,
  AccountTier,
  AuditLogRecord,
  AuditEventType
} from '../types/user';
import { evaluateResourceAccess, AuthorizationDecision } from '../utils/permissionMatrix';
import { CompleteKundali } from '../types/kundali';

// Client-side local key constants
const PROFILES_STORAGE_PREFIX = 'vedang_profiles_';
const CONSULTATIONS_STORAGE_KEY = 'vedang_consultations_store';
const AUDIT_LOGS_STORAGE_KEY = 'vedang_audit_logs_v2';
const GUEST_QUOTA_STORAGE_KEY = 'vedang_guest_query_count';

/**
 * Built-in Synthetic Vedic Practice Charts (Strictly isolated training data)
 */
export const SYNTHETIC_PRACTICE_CHARTS: PracticeKundaliRecord[] = [
  {
    id: 'prc_001_gajakesari',
    title: 'Gajakesari & Dharma-Karmadhipati Yoga Study',
    category: 'Yoga Study',
    learningObjective: 'Analyze Guru in Kendra to Chandra with exalted 9th and 10th lords forming Raja Yoga in 10th house.',
    birthDetails: {
      name: 'PRACTICE / TRAINING: Chart Alpha (Gajakesari)',
      gender: 'male',
      date: '1985-05-15',
      time: '09:30',
      placeName: 'Varanasi, India',
      latitude: 25.3176,
      longitude: 82.9739,
      timezoneOffsetMinutes: 330
    },
    notes: 'Classic example of Jupiter-Moon mutual 4th/10th aspect generating strong auspicious Raja Yoga. Practice D9 Navamsha confirmation.',
    isPracticeData: true
  },
  {
    id: 'prc_002_vipareeta',
    title: 'Vipareeta Raja Yoga & Sarala Yoga Case',
    category: 'Dasha Timing',
    learningObjective: 'Examine 8th lord in 6th house (Sarala Yoga) and transition during Shani-Rahu dasha period.',
    birthDetails: {
      name: 'PRACTICE / TRAINING: Chart Beta (Vipareeta)',
      gender: 'female',
      date: '1992-11-20',
      time: '18:45',
      placeName: 'New Delhi, India',
      latitude: 28.6139,
      longitude: 77.209,
      timezoneOffsetMinutes: 330
    },
    notes: 'Dusthana lords placed in mutual Dusthana houses giving rise to sudden elevation after adversity.',
    isPracticeData: true
  },
  {
    id: 'prc_003_budhaditya',
    title: 'Budhaditya & Bhadra Mahapurusha Yoga in Virgo',
    category: 'Varga Analysis',
    learningObjective: 'Study exalted Mercury in 1st house forming Bhadra Yoga in D1 and D10 Dashamsha career elevation.',
    birthDetails: {
      name: 'PRACTICE / TRAINING: Chart Gamma (Bhadra Yoga)',
      gender: 'male',
      date: '1990-09-22',
      time: '06:15',
      placeName: 'Ujjain, India',
      latitude: 23.1765,
      longitude: 75.7885,
      timezoneOffsetMinutes: 330
    },
    notes: 'Exalted Budha in Kanya Lagna giving analytical intellect and high mathematical precision.',
    isPracticeData: true
  }
];

/**
 * Audit Logging Function with Cryptographic Hash verification
 */
export function recordAuditLog(params: {
  eventType: AuditEventType;
  user: UserProfile | null;
  resourceType: 'user' | 'profile' | 'kundali' | 'consultation' | 'system' | 'ai_chat';
  resourceId?: string;
  action: string;
  decision: 'ALLOW' | 'DENY';
  reason?: string;
  details: string;
}): AuditLogRecord {
  const timestamp = new Date().toISOString();
  const userId = params.user?.uid || 'guest_user';
  const userEmail = params.user?.email || 'guest@anonymous';
  const userRole = params.user?.role || UserRole.GUEST;

  const eventId = `AUD_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

  // Tamper-evident hash string
  const rawStr = `${eventId}|${timestamp}|${params.eventType}|${userId}|${params.decision}|${params.details}`;
  let hash = 0;
  for (let i = 0; i < rawStr.length; i++) {
    hash = (hash << 5) - hash + rawStr.charCodeAt(i);
    hash |= 0;
  }
  const integrityHash = `HASH_${Math.abs(hash).toString(16).toUpperCase()}`;

  const record: AuditLogRecord = {
    eventId,
    timestamp,
    eventType: params.eventType,
    userId,
    userEmail,
    userRole,
    resourceType: params.resourceType,
    resourceId: params.resourceId,
    action: params.action,
    decision: params.decision,
    reason: params.reason,
    details: params.details,
    integrityHash
  };

  try {
    const raw = localStorage.getItem(AUDIT_LOGS_STORAGE_KEY);
    const list: AuditLogRecord[] = raw ? JSON.parse(raw) : [];
    list.unshift(record);
    if (list.length > 500) list.length = 500;
    localStorage.setItem(AUDIT_LOGS_STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    console.warn('Audit logging storage note:', e);
  }

  // Also asynchronously relay to server audit trail endpoint if available
  try {
    fetch('/api/audit-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(record)
    }).catch(() => {});
  } catch {}

  return record;
}

/**
 * Get all audit log entries (Super Admin & Admin accessible)
 */
export function getStoredAuditLogs(): AuditLogRecord[] {
  try {
    const raw = localStorage.getItem(AUDIT_LOGS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Profile Management: Fetch only authorized profiles for a user (Self + Family Profiles)
 */
export function getUserAuthorizedProfiles(user: UserProfile | null): UserProfileRecord[] {
  if (!user) {
    return [];
  }

  try {
    const key = `${PROFILES_STORAGE_PREFIX}${user.uid}`;
    const raw = localStorage.getItem(key);
    let profiles: UserProfileRecord[] = raw ? JSON.parse(raw) : [];

    // Ensure Default Self Profile exists
    if (!profiles.some((p) => p.relationship === 'self' && !p.isDeleted)) {
      const defaultSelf: UserProfileRecord = {
        profileId: `prof_self_${user.uid.substring(0, 8)}`,
        ownerUserId: user.uid,
        name: user.displayName || 'Self Profile',
        relationship: 'self',
        gender: 'male',
        date: '1990-01-01',
        time: '12:00',
        placeName: 'New Delhi, India',
        latitude: 28.6139,
        longitude: 77.209,
        timezone: 330,
        createdAt: user.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      profiles.unshift(defaultSelf);
      localStorage.setItem(key, JSON.stringify(profiles));
    }

    // Return non-deleted profiles strictly owned by this user
    return profiles.filter((p) => p.ownerUserId === user.uid && !p.isDeleted);
  } catch (e) {
    console.error('Error fetching authorized profiles:', e);
    return [];
  }
}

/**
 * Save or Update a Family / Self Profile with Ownership verification
 */
export function saveUserProfileRecord(
  currentUser: UserProfile,
  profile: Partial<UserProfileRecord> & { name: string; relationship: any }
): UserProfileRecord {
  const key = `${PROFILES_STORAGE_PREFIX}${currentUser.uid}`;
  const raw = localStorage.getItem(key);
  const profiles: UserProfileRecord[] = raw ? JSON.parse(raw) : [];

  const profileId = profile.profileId || `prof_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
  const existingIdx = profiles.findIndex((p) => p.profileId === profileId);

  const fullRecord: UserProfileRecord = {
    profileId,
    ownerUserId: currentUser.uid, // Strict resource ownership
    name: profile.name.trim(),
    relationship: profile.relationship || 'family',
    gender: profile.gender || 'male',
    date: profile.date || '1990-01-01',
    time: profile.time || '12:00',
    placeName: profile.placeName || 'New Delhi, India',
    latitude: profile.latitude ?? 28.6139,
    longitude: profile.longitude ?? 77.209,
    timezone: profile.timezone ?? 330,
    notes: profile.notes || '',
    isPractice: false,
    createdAt: existingIdx >= 0 ? profiles[existingIdx].createdAt : new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  if (existingIdx >= 0) {
    // Verify ownership before modifying
    if (profiles[existingIdx].ownerUserId !== currentUser.uid && currentUser.role !== UserRole.SUPER_ADMIN) {
      recordAuditLog({
        eventType: 'AUTHZ_DENIED',
        user: currentUser,
        resourceType: 'profile',
        resourceId: profileId,
        action: 'UPDATE_PROFILE',
        decision: 'DENY',
        details: 'Attempted to edit profile owned by another user.'
      });
      throw new Error('Unauthorized: You do not own this profile.');
    }
    profiles[existingIdx] = fullRecord;
    recordAuditLog({
      eventType: 'PROFILE_UPDATED',
      user: currentUser,
      resourceType: 'profile',
      resourceId: profileId,
      action: 'UPDATE_PROFILE',
      decision: 'ALLOW',
      details: `Updated ${fullRecord.relationship} profile for ${fullRecord.name}`
    });
  } else {
    profiles.push(fullRecord);
    recordAuditLog({
      eventType: 'PROFILE_CREATED',
      user: currentUser,
      resourceType: 'profile',
      resourceId: profileId,
      action: 'CREATE_PROFILE',
      decision: 'ALLOW',
      details: `Created new ${fullRecord.relationship} profile: ${fullRecord.name}`
    });
  }

  localStorage.setItem(key, JSON.stringify(profiles));
  return fullRecord;
}

/**
 * Soft Delete a Profile with Administrative Retention
 */
export function softDeleteProfile(currentUser: UserProfile, profileId: string): void {
  const key = `${PROFILES_STORAGE_PREFIX}${currentUser.uid}`;
  const raw = localStorage.getItem(key);
  const profiles: UserProfileRecord[] = raw ? JSON.parse(raw) : [];

  const target = profiles.find((p) => p.profileId === profileId);
  if (!target) return;

  if (target.ownerUserId !== currentUser.uid && currentUser.role !== UserRole.SUPER_ADMIN) {
    recordAuditLog({
      eventType: 'AUTHZ_DENIED',
      user: currentUser,
      resourceType: 'profile',
      resourceId: profileId,
      action: 'DELETE_PROFILE',
      decision: 'DENY',
      details: 'Unauthorized attempt to delete profile.'
    });
    throw new Error('Unauthorized: Cannot delete profile owned by another user.');
  }

  target.isDeleted = true;
  target.deletedAt = new Date().toISOString();
  target.updatedAt = new Date().toISOString();

  localStorage.setItem(key, JSON.stringify(profiles));

  recordAuditLog({
    eventType: 'PROFILE_DELETED',
    user: currentUser,
    resourceType: 'profile',
    resourceId: profileId,
    action: 'SOFT_DELETE_PROFILE',
    decision: 'ALLOW',
    details: `Soft-deleted profile ${target.name} (${target.relationship}). Retained in administrative audit trail.`
  });
}

/**
 * Consultation Assignment Store & Verification
 */
export function getActiveConsultationsForAstrologer(astrologerUid: string): ConsultationRecord[] {
  try {
    const raw = localStorage.getItem(CONSULTATIONS_STORAGE_KEY);
    const all: ConsultationRecord[] = raw ? JSON.parse(raw) : [];
    const now = new Date().getTime();

    return all.filter((c) => {
      if (c.consultantAstrologerId !== astrologerUid) return false;
      if (c.status !== 'active') return false;
      const exp = new Date(c.expiryTime).getTime();
      return now <= exp;
    });
  } catch {
    return [];
  }
}

/**
 * Create or Assign a Consultation (Admin / Customer Action)
 */
export function assignConsultation(params: {
  consultantAstrologerId: string;
  customerUser: UserProfile;
  authorizedProfile: UserProfileRecord;
  authorizedKundaliId: string;
  scope?: 'full_reading' | 'dasha_analysis' | 'remedies' | 'muhurta' | 'general_query';
  durationHours?: number;
  customerQuestions?: string[];
}): ConsultationRecord {
  const now = new Date();
  const durationMs = (params.durationHours || 48) * 60 * 60 * 1000;
  const expiry = new Date(now.getTime() + durationMs);

  const consultationId = `cns_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

  const record: ConsultationRecord = {
    consultationId,
    consultantAstrologerId: params.consultantAstrologerId,
    customerUserId: params.customerUser.uid,
    customerDisplayName: params.customerUser.displayName || params.customerUser.email,
    authorizedProfileId: params.authorizedProfile.profileId,
    authorizedKundaliId: params.authorizedKundaliId,
    scope: params.scope || 'full_reading',
    startTime: now.toISOString(),
    expiryTime: expiry.toISOString(),
    status: 'active',
    customerQuestions: params.customerQuestions || [
      'Career trajectory in current Mahadasha',
      'Favorable gemstone and planetary remedies'
    ],
    createdAt: now.toISOString(),
    updatedAt: now.toISOString()
  };

  try {
    const raw = localStorage.getItem(CONSULTATIONS_STORAGE_KEY);
    const all: ConsultationRecord[] = raw ? JSON.parse(raw) : [];
    all.unshift(record);
    localStorage.setItem(CONSULTATIONS_STORAGE_KEY, JSON.stringify(all));
  } catch (e) {
    console.error('Error storing consultation record:', e);
  }

  recordAuditLog({
    eventType: 'CONSULTATION_ASSIGNED',
    user: params.customerUser,
    resourceType: 'consultation',
    resourceId: consultationId,
    action: 'ASSIGN_CONSULTATION',
    decision: 'ALLOW',
    details: `Assigned consultation to astrologer [${params.consultantAstrologerId}] for customer [${params.customerUser.email}], Profile: [${params.authorizedProfile.name}]`
  });

  return record;
}

/**
 * Revoke or End Consultation Immediately
 */
export function revokeConsultation(user: UserProfile, consultationId: string, reason: string = 'User completed or revoked consultation'): void {
  try {
    const raw = localStorage.getItem(CONSULTATIONS_STORAGE_KEY);
    const all: ConsultationRecord[] = raw ? JSON.parse(raw) : [];
    const target = all.find((c) => c.consultationId === consultationId);

    if (target) {
      target.status = 'revoked';
      target.updatedAt = new Date().toISOString();
      localStorage.setItem(CONSULTATIONS_STORAGE_KEY, JSON.stringify(all));

      recordAuditLog({
        eventType: 'CONSULTATION_REVOKED',
        user,
        resourceType: 'consultation',
        resourceId: consultationId,
        action: 'REVOKE_CONSULTATION',
        decision: 'ALLOW',
        details: `Consultation revoked: ${reason}`
      });
    }
  } catch (e) {
    console.error('Error revoking consultation:', e);
  }
}
